import { Link, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Calculator, Download, FileText, Euro, Clock, ChevronDown, ChevronUp, TrendingDown, TableProperties, Package } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSelectedMissions } from "@/hooks/useSelectedMissions";
import { useFinalisationParams } from "@/hooks/useFinalisationParams";
import { useClientData } from "@/services/clientDataService";
import { useNavigationPricing } from "@/hooks/useNavigationPricing";
import type { PricingMode } from "@/utils/packAilixPricing";
import { deduplicateSelectedMissionItems, countLogicalMissions } from "@/utils/subMissionUtils";
import type { Mission } from "@/services/clientDataService";
import { calculateHybridPrestationsBreakdown, calculatePackAilixEligibility } from "@/utils/packAilixPricing";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger
} from "@/components/ui/drawer";
import { Menu } from "lucide-react";
import { NavigationSummaryPanel, NavigationSummaryContent } from "@/components/navigation/NavigationSummaryPanel";
import type { MissionRoiByMissionId } from "@/utils/realtimeMissionRoi";

interface NavigationProps {
  selectedItems?: Array<{id: string; type: 'mission' | 'option'; quantity: number}>;
  pricing?: {total: number; discounts: Array<{name: string; amount: number}>};
  estimatedDuration?: string;
  onSign?: () => void;
  onExport?: () => void;
  onExportMissionsPdf?: () => void;
  allMissions?: Array<{id: string; title: string; duration?: string; duration_days?: number; price?: number}>;
  allOptions?: Array<{id: string; title: string}>;
  dynamicOptions?: Array<{id: string; option_id?: string; title: string; price: number; is_included: boolean}>;
  roiData?: {
    roi3Years: number;
    roiAnnual: number;
  };
  roiEnabled?: boolean;
  onRemoveItem?: (id: string, type: 'mission' | 'option') => void;
  /** When false, only render the navigation tabs without the summary panel/drawer */
  showSummaryPanel?: boolean;
  /** ROI par mission et par mode de tarification. */
  roiByMissionId?: MissionRoiByMissionId;
}

const Navigation = ({ selectedItems: propsSelectedItems, pricing: propsPricing, estimatedDuration: propsEstimatedDuration, onSign, onExport, onExportMissionsPdf, allMissions: propsAllMissions = [], allOptions = [], dynamicOptions = [], roiData: propsRoiData, roiEnabled = true, onRemoveItem, showSummaryPanel = true, roiByMissionId }: NavigationProps = {}) => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMobile = useIsMobile();

  // Détection tablette : écrans < 1024px doivent utiliser l'interface mobile
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(false);

  useEffect(() => {
    const checkSize = () => {
      setIsTabletOrMobile(window.innerWidth < 1024);
    };
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  const { clientId } = useParams();
  const { params: finalisationParams, setParams: setFinalisationParams } = useFinalisationParams(clientId || "");

  const setSharedPricingMode = useCallback<React.Dispatch<React.SetStateAction<PricingMode>>>((nextMode) => {
    const resolvedMode = typeof nextMode === 'function'
      ? nextMode(finalisationParams.offerMode)
      : nextMode;
    setFinalisationParams({ offerMode: resolvedMode });
  }, [finalisationParams.offerMode, setFinalisationParams]);

  // Récupérer les sélections globales depuis localStorage
  const { selectedItems: globalSelectedItems } = useSelectedMissions(clientId, "Navigation");
  const { data: clientData } = useClientData(clientId);

  // Priorité aux props pour la synchronisation temps réel avec MissionsConfigurateur
  const selectedItems = propsSelectedItems !== undefined ? propsSelectedItems : globalSelectedItems;

  // Utiliser allMissions des props ou des données client (INCLURE foundations)
  const allMissions = useMemo(() => {
    if (propsAllMissions && propsAllMissions.length > 0) return propsAllMissions;
    if (!clientData) return [];
    return [
      ...(Array.isArray(clientData.missions) ? clientData.missions : []),
      ...(Array.isArray(clientData.additionalMissions) ? clientData.additionalMissions : []),
      ...(Array.isArray(clientData.foundations) ? clientData.foundations : [])
    ];
  }, [propsAllMissions, clientData]);

  // ============================================================================
  // PRICING via hook dédié (remplace ~250 lignes de logique inline)
  // ============================================================================
  const {
    pricingMode,
    setPricingMode,
    pricing,
    totalDays,
    packAilixEligibility,
    calculatedEconomies,
    totalEtpSaved,
    annualCosts,
    includedOptionsCost,
    comparisonModel,
  } = useNavigationPricing({
    selectedItems,
    allMissions,
    dynamicOptions,
    propsPricing,
    clientData,
    pricingMode: finalisationParams.offerMode,
    setPricingMode: setSharedPricingMode,
    pricingNbDevelopers: finalisationParams.nbDevelopers,
    enabledPrestations: finalisationParams.enabledPrestations,
  });

  // En mode hybride, recalculer le total jours = dev + prestations (MOE, Pilotage…)
  // pour que le client voie le vrai nombre de jours facturés.
  const estimatedDuration = useMemo(() => {
    const packElig = calculatePackAilixEligibility(totalDays);
    if (comparisonModel !== 'hybrid' || totalDays <= 0 || !packElig.eligible) {
      return propsEstimatedDuration;
    }
    const nbDevs = finalisationParams.nbDevelopers > 0 ? finalisationParams.nbDevelopers : 2;
    const packConfig = clientData?.pack_ailix_config ?? null;

    const breakdown = calculateHybridPrestationsBreakdown(
      totalDays, nbDevs, pricingMode, packConfig,
      clientData?.tjm_moe ?? undefined,
      finalisationParams.enabledPrestations,
    );
    const prestaDays = Object.values(breakdown).reduce(
      (sum, p) => sum + p.joursTotalProjet, 0,
    );
    const total = Math.round(totalDays + prestaDays);
    return `${total} jours`;
  }, [comparisonModel, totalDays, propsEstimatedDuration, pricingMode, clientData, finalisationParams]);

  // ROI : utiliser les données calculées ou désactiver l'affichage si indisponible
  const roi3Years = propsRoiData?.roi3Years ?? 0;
  const roiAnnual = propsRoiData?.roiAnnual ?? 0;
  const base = clientId ? `/${clientId}` : "";
  // Navigation différente selon si on est authentifié ou pas  
  const isPilotage = clientData?.pilotage_enabled === true;
  const navItems = clientId ? [
    { label: "Accueil", href: `/${clientId}` },
    { label: "Synthèse", href: `${base}/synthese` },
    { label: "Missions & Config", href: `${base}/missions` },
    isPilotage
      ? { label: "Pilotage", href: `${base}/pilotage` }
      : { label: "Finalisation", href: `${base}/finalisation` },
    { label: "Qui sommes nous", href: `${base}/cabinet` },
  ] : [
    { label: "Accueil", href: "/" },
    { label: "Qui sommes nous", href: "/cabinet" },
  ];

  const hasSelection = Array.isArray(selectedItems) && selectedItems.length > 0;

  // Comptage missions "logiques" : chaque groupe = 1, chaque standalone = 1
  const deduplicatedMissionCount = useMemo(() => {
    if (!Array.isArray(selectedItems) || selectedItems.length === 0) return 0;
    return countLogicalMissions(selectedItems, allMissions as Mission[]);
  }, [selectedItems, allMissions]);

  const packLabel = pricingMode === 'pack_ailix_1an' ? 'Ailix 1 an'
    : pricingMode === 'pack_ailix_2ans' ? 'Ailix 2 ans'
    : 'À la carte';

  return (
    <>
      <nav className="bg-gradient-card border-b border-border shadow-card backdrop-blur-lg sticky top-0 z-50">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between h-16 gap-2">
            {/* Logo + Pack type */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link to="/" className="flex items-center group">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center overflow-hidden bg-white/10
                              transition-all duration-200 ease-out
                              group-hover:shadow-card group-hover:bg-white/15">
                  <img
                    src="/lovable-uploads/1c3850cb-8bb3-4ca7-a0a2-bdb44846479f.png"
                    alt="Ailix Consulting"
                    className="w-full h-full object-contain transition-transform duration-200 group-hover:scale-105"
                  />
                </div>
              </Link>
              {clientId && (
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs whitespace-nowrap transition-colors",
                    pricingMode === 'a_la_carte'
                      ? "text-muted-foreground border-muted-foreground/30 hover:text-foreground hover:border-muted-foreground/50"
                      : "bg-primary/10 text-primary border-primary/25 hover:bg-primary/15 hover:border-primary/40"
                  )}
                >
                  <Package className="w-3 h-3 mr-1" />
                  {packLabel}
                </Badge>
              )}
            </div>
            
            {/* Navigation Desktop */}
            {!isTabletOrMobile && (
              <div className="flex items-center space-x-1">
                {navItems.map((item) => (
                  <Button
                    key={item.href}
                    variant={location.pathname === item.href ? "default" : "ghost"}
                    asChild
                    className={cn(
                      "relative transition-all duration-200 ease-out",
                      "hover:shadow-elegant hover:-translate-y-0.5",
                      location.pathname === item.href ? "bg-primary/10 text-primary hover:text-primary-foreground shadow-card" : 
                      "hover:bg-accent/5 hover:text-primary"
                    )}
                  >
                    <Link to={item.href}>{item.label}</Link>
                  </Button>
                ))}
              </div>
            )}

            {/* Zone droite : Synthèse + Menu hamburger sur mobile */}
            <div className="flex items-center gap-2">
              {/* Résumé projet ou bouton démarrer */}
              {showSummaryPanel && hasSelection && pricing ? (
                isTabletOrMobile ? (
                  <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
                    <DrawerTrigger asChild>
                      <Button size="sm" variant="outline" className="h-9 px-2 sm:px-3 flex-shrink-0">
                        <Calculator className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Synthèse</span>
                        <Badge variant="secondary" className="ml-1 text-xs">
                          {pricing.total.toLocaleString()}€
                        </Badge>
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent className="max-h-[90vh]">
                      <DrawerHeader className="text-left">
                        <DrawerTitle className="flex items-center gap-3">
                          <Calculator className="w-5 h-5 text-primary" />
                          Synthèse du projet
                        </DrawerTitle>
                      </DrawerHeader>
                      <NavigationSummaryContent
                        pricing={pricing}
                        estimatedDuration={estimatedDuration}
                        selectedItems={selectedItems}
                        allMissions={allMissions}
                        allOptions={allOptions}
                        dynamicOptions={dynamicOptions}
                        roiEnabled={roiEnabled}
                        roi3Years={roi3Years}
                        roiAnnual={roiAnnual}
                        clientId={clientId}
                        clientData={clientData}
                        pricingMode={pricingMode}
                        setPricingMode={setPricingMode}
                        pricingNbDevelopers={finalisationParams.nbDevelopers}
                        enabledPrestations={finalisationParams.enabledPrestations}
                        packAilixEligibility={packAilixEligibility}
                        calculatedEconomies={calculatedEconomies}
                        totalEtpSaved={totalEtpSaved}
                        annualCosts={annualCosts}
                        includedOptionsCost={includedOptionsCost}
                        onRemoveItem={onRemoveItem}
                        onSign={onSign}
                        onExport={onExport}
                        onExportMissionsPdf={onExportMissionsPdf}
                        roiByMissionId={roiByMissionId}
                      />
                    </DrawerContent>
                  </Drawer>
                ) : (
                <div 
                  className="flex items-center gap-2 lg:gap-4 bg-primary/5 px-2 lg:px-4 py-2 rounded-lg border cursor-pointer 
                            transition-all duration-300 ease-out group
                            hover:bg-primary/10 hover:border-primary/30 hover:shadow-elegant max-w-full"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
                    <Calculator className="w-4 h-4 text-primary transition-transform duration-200 group-hover:scale-110" />
                    <Badge variant="outline" className="text-xs transition-all duration-200 group-hover:bg-primary/10 hidden xl:inline-flex">
                      {deduplicatedMissionCount} éléments
                    </Badge>
                  </div>
                <div className="flex items-center gap-2 lg:gap-4 text-sm overflow-hidden">
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1 transition-transform duration-200 hover:scale-105 flex-shrink-0">
                          <Euro className="w-4 h-4 text-accent" />
                          <span className="font-medium whitespace-nowrap">{pricing.total.toLocaleString()}€</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Coût total HT du projet</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {roiEnabled && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="hidden xl:flex items-center gap-1 transition-transform duration-200 hover:scale-105 flex-shrink-0">
                          <TrendingDown className="w-4 h-4 text-green-500" />
                          <span className="text-green-600 font-medium whitespace-nowrap">{calculatedEconomies.savings3Years.toLocaleString()}€</span>
                          <span className="text-xs text-muted-foreground">/3ans</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Économies estimées sur 3 ans grâce aux missions sélectionnées</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  )}
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="hidden lg:flex items-center gap-1 transition-transform duration-200 hover:scale-105 flex-shrink-0">
                          <Clock className="w-4 h-4 text-primary" />
                          <span className="whitespace-nowrap">{estimatedDuration}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Durée totale estimée du projet</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <div className="hidden 2xl:flex gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="hero" className="h-8 transition-all duration-200 ease-out 
                               hover:shadow-glow" onClick={onSign}>
                      <FileText className="w-3 h-3 mr-1" />
                      Envoyer
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" className="h-8 bg-white/90 text-foreground
                                   hover:bg-white hover:text-primary hover:border-white hover:shadow-glow transition-all duration-200">
                          <Download className="w-3 h-3" />
                          Télécharger
                          <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={onExport}>
                          <FileText className="w-4 h-4 mr-2" />
                          Télécharger le devis
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={onExportMissionsPdf}>
                          <TableProperties className="w-4 h-4 mr-2" />
                          Télécharger un récapitulatif
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="transition-all duration-200 group-hover:scale-110 flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground ml-1 lg:ml-2" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground ml-1 lg:ml-2" />
                    )}
                  </div>
                </div>
                </div>
              )
              ) : null}
              
              {/* Menu hamburger sur mobile/tablette */}
              {isTabletOrMobile && (
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="flex-shrink-0">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right">
                    <SheetHeader>
                      <SheetTitle>Menu</SheetTitle>
                    </SheetHeader>
                    <div className="flex flex-col space-y-2 mt-4">
                      {navItems.map((item) => (
                        <Button
                          key={item.href}
                          variant={location.pathname === item.href ? "default" : "ghost"}
                          asChild
                          className="justify-start"
                        >
                          <Link to={item.href}>{item.label}</Link>
                        </Button>
                      ))}
                    </div>
                  </SheetContent>
                </Sheet>
              )}
            </div>
          </div>
        </div>
      </nav>
      
      {showSummaryPanel && hasSelection && pricing && (
        <NavigationSummaryPanel
          isExpanded={isExpanded}
          onClose={() => setIsExpanded(false)}
          hasSelection={hasSelection}
          pricing={pricing}
          estimatedDuration={estimatedDuration}
          selectedItems={selectedItems}
          allMissions={allMissions}
          allOptions={allOptions}
          dynamicOptions={dynamicOptions}
          roiEnabled={roiEnabled}
          roi3Years={roi3Years}
          roiAnnual={roiAnnual}
          clientId={clientId}
          clientData={clientData}
          pricingMode={pricingMode}
          setPricingMode={setPricingMode}
          pricingNbDevelopers={finalisationParams.nbDevelopers}
          enabledPrestations={finalisationParams.enabledPrestations}
          packAilixEligibility={packAilixEligibility}
          calculatedEconomies={calculatedEconomies}
          totalEtpSaved={totalEtpSaved}
          annualCosts={annualCosts}
          includedOptionsCost={includedOptionsCost}
          onRemoveItem={onRemoveItem}
          onSign={onSign}
          onExport={onExport}
          onExportMissionsPdf={onExportMissionsPdf}
          roiByMissionId={roiByMissionId}
        />
      )}
    </>
  );
};

export default Navigation;
